import { LostStrand } from "@/pages/caster/models/caster.models";
import {
	deleteLostStrand,
	upsertLostStrand,
} from "@/pages/caster/repositories/ProductionRepository";
import { useModalStyles } from "@/pages/caster/styles/modalStyles";
import { DateTimePickerDE } from "@/pages/eaf/data-entry/components/form-fields/common/DateTimePickerDE";
import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	MesfModal,
	getMomentTz,
} from "@dexteel/mesf-core";
import {
	Button,
	FormControl,
	Grid2 as Grid,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LostStrandTypeSelector } from "../components/LostStrandTypeSelector";

type Props = {
	show: "create" | "edit" | "delete" | null;
	onHide: () => void;
	date: Date;
	limitDates?: {
		start: Date;
		end: Date | null;
	};
	lostStrand?: LostStrand;
	processId?: number;
};
const moment = getMomentTz();
export const UpsertDeleteLostStrand = ({
	show,
	onHide,
	date,
	limitDates,
	lostStrand,
	processId,
}: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(false);
	const classes = useModalStyles();
	const helmetTitle = useMemo(() => {
		if (show === "edit") {
			return "Edit Lost Strand";
		} else if (show === "delete") {
			return "Delete Lost Strand";
		} else if (show === "create") {
			return "New Lost Strand";
		}
	}, [show]);
	const title = useMemo(() => {
		if (show === "edit") {
			return "EDIT LOST STRAND";
		} else if (show === "delete") {
			return "DELETE LOST STRAND";
		} else if (show === "create") {
			return "NEW LOST STRAND";
		}
	}, [show]);
	const {
		getValues,
		handleSubmit,
		setValue,
		control,
		reset,
		formState: { errors },
	} = useForm<LostStrand>({});

	const onSubmit: SubmitHandler<LostStrand> = async (data: LostStrand) => {
		setIsSubmitLoading(true);
		if (show === "delete") {
			const resp = await deleteLostStrand(data.LostStrandId);
			if (resp.ok) {
				setOpen(true);
				onHide();
			} else {
				setError(resp.message);
			}
		} else {
			const resp = await upsertLostStrand(data);
			if (resp.ok) {
				setOpen(true);
				onHide();
			} else {
				setError(resp.message);
			}
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (show) {
			if ((show === "edit" || show === "delete") && lostStrand) {
				reset({
					...lostStrand,
					Timestamp: lostStrand.Timestamp
						? moment.utc(lostStrand.Timestamp).toDate()
						: date,
				});
			} else if (show === "create") {
				reset({
					ProcessId: processId,
					Timestamp: date,
					LostStrandId: 0,
					StrandId: undefined,
					LostStrandTypeId: undefined,
					Comment: "",
					Author: "",
				});
			}
		}
	}, [show, lostStrand, processId, date, reset]);
	return (
		<>
			{show && <HelmetDexteel title={`${helmetTitle}`} />}
			<Grid container>
				<Grid>
					<MesfModal
						title={title}
						open={!!show}
						handleClose={() => onHide()}
						id="upsert-electrode-event-modal"
						maxWidth="sm"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, md: 6 }}>
										<Controller
											name="StrandId"
											control={control}
											rules={{
												required: "Strand is required",
											}}
											render={({ field }) => (
												<FormControl variant="outlined" fullWidth>
													<InputLabel id="strandSelector">Strand</InputLabel>
													<Select
														fullWidth
														labelId="strandSelector"
														id="strandSelector"
														value={field.value}
														onChange={field.onChange}
														label="Strand"
														disabled={show === "delete"}
													>
														<MenuItem value={1}>Strand 1</MenuItem>
														<MenuItem value={2}>Strand 2</MenuItem>
														<MenuItem value={3}>Strand 3</MenuItem>
													</Select>
												</FormControl>
											)}
										/>
										{errors.StrandId && (
											<span className={classes.errorLabel}>
												{errors.StrandId.message}
											</span>
										)}
									</Grid>

									<Grid size={{ xs: 12, md: 6 }} style={{ marginTop: "2px" }}>
										<Controller
											name="Timestamp"
											control={control}
											defaultValue={date}
											rules={{
												required: "Timestamp is required",
												validate: (value) => {
													if (limitDates?.start && value < limitDates.start) {
														return "Timestamp must be between the start and end of the process";
													} else if (
														limitDates?.end &&
														value > limitDates.end
													) {
														return "Timestamp must be less than the end of the process";
													} else {
														return true;
													}
												},
											}}
											render={({ field }) => (
												<DateTimePickerDE
													{...field}
													format={"HH:mm - MM/DD/YYYY"}
													value={field.value}
													label="Time"
													error={errors.Timestamp?.message}
													onChange={(val) => {
														if (val) {
															setValue("Timestamp", val, {
																shouldValidate: true,
															});
														}
													}}
													onBlur={() => {}}
													disabled={show === "delete"}
												/>
											)}
										/>
									</Grid>
									<Grid size={{ xs: 12, md: 12 }}>
										<Controller
											name="LostStrandTypeId"
											control={control}
											rules={{
												required: "Lost Strand Type is required",
												validate: (value) => {
													if (!value || value === 0) {
														return "Lost Strand Type is required";
													}
													return true;
												},
											}}
											render={({ field }) => (
												<LostStrandTypeSelector
													value={field.value}
													onChange={field.onChange}
													error={errors.LostStrandTypeId?.message}
													disabled={show === "delete" ? true : false}
													helperText={errors.LostStrandTypeId?.message}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid size={{ xs: 12, md: 6 }} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={() => onHide()}
											size="large"
										>
											Cancel
										</Button>
									</Grid>
									<Grid size={{ xs: 12, md: 6 }} style={{ margin: 0 }}>
										<ButtonWithLoading
											fullWidth
											isLoading={isSubmitLoading}
											variant="contained"
											color={show === "delete" ? "error" : "primary"}
											type="submit"
											size="large"
										>
											{show === "delete" ? "Delete" : "Save"}
										</ButtonWithLoading>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
